import { useEffect, useState } from "react";
import { Alert, LinearProgress, Stack, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useGetRedactionStatusQuery } from "src/store/api/generatedApi";

type RedactionJobProgressProps = Readonly<{
  jobId: string;
}>;

const STATUS_POLLING_INTERVAL_MS = 2000;

export const RedactionJobProgress = ({ jobId }: RedactionJobProgressProps) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const [isTerminal, setIsTerminal] = useState(false);

  const { data: statusData } = useGetRedactionStatusQuery(
    { tenantName, jobId },
    {
      pollingInterval: isTerminal ? 0 : STATUS_POLLING_INTERVAL_MS,
      skip: !jobId,
    }
  );

  const isComplete = statusData?.status === "finished";
  const isFailed = statusData?.status === "failed";
  const hasErrors = (statusData?.errors.length ?? 0) > 0;

  useEffect(() => {
    if (isComplete || isFailed) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- one-way latch to stop polling once job finishes
      setIsTerminal(true);
    }
  }, [isComplete, isFailed]);

  if (!statusData) {
    return <LinearProgress />;
  }

  const progressPercent =
    statusData.total > 0 ? (statusData.progress / statusData.total) * 100 : 0;

  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        {t("redactFootageProgress", {
          progress: statusData.progress,
          total: statusData.total,
        })}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={progressPercent}
        color={
          isFailed
            ? "error"
            : isComplete && hasErrors
              ? "warning"
              : isComplete
                ? "success"
                : "primary"
        }
      />
      {isComplete && !hasErrors ? (
        <Alert severity="success">{t("redactFootageComplete")}</Alert>
      ) : null}
      {isComplete && hasErrors ? (
        <Alert severity="error">{statusData.errors.join(", ")}</Alert>
      ) : null}
      {isFailed ? (
        <Alert severity="error">
          {statusData.errors.length > 0
            ? statusData.errors.join(", ")
            : t("redactFootageFailed")}
        </Alert>
      ) : null}
    </Stack>
  );
};
